import type { PriceTable, Slot } from '../production';
import type { Cost } from '../types/cards';
import type { Resource, ResourceCount } from '../types/resources';
import { RESOURCES, normalizeCount } from '../types/resources';
import { canCover } from './matching';

export interface PaymentContext {
  /** Monedas al inicio del turno (las ganadas durante el turno no cuentan). */
  coins: number;
  own: Slot[];
  left: Slot[];
  right: Slot[];
  prices: { left: PriceTable; right: PriceTable };
}

export interface PaymentOption {
  buy: { left: ResourceCount; right: ResourceCount };
  coins: { bank: number; left: number; right: number };
  total: number;
}

export type PaymentBlock = 'NO_RESOURCES' | 'NOT_ENOUGH_COINS';

export type PaymentResult = { ok: true; options: PaymentOption[] } | { ok: false; reason: PaymentBlock };

function toUnits(rc: ResourceCount): Resource[] {
  const out: Resource[] = [];
  for (const r of RESOURCES) for (let i = 0; i < (rc[r] ?? 0); i++) out.push(r);
  return out;
}

function priceOf(rc: ResourceCount, table: PriceTable): number {
  let n = 0;
  for (const r of RESOURCES) n += (rc[r] ?? 0) * table[r];
  return n;
}

/**
 * Todas las formas razonables de pagar `cost`: el frente de Pareto en
 * (monedas al vecino izquierdo, monedas al vecino derecho), ordenado por coste total.
 */
export function computePaymentOptions(ctx: PaymentContext, cost: Cost): PaymentResult {
  const bank = cost.coins ?? 0;
  const need: ResourceCount = { ...(cost.resources ?? {}) };

  // 1) Producción fija propia: siempre óptimo usarla primero.
  const ownChoice: Slot[] = [];
  for (const s of ctx.own) {
    if (s.options.length === 1) {
      const r = s.options[0] as Resource;
      if ((need[r] ?? 0) > 0) need[r] = (need[r] as number) - 1;
    } else {
      ownChoice.push(s);
    }
  }
  const remaining = RESOURCES.filter((r) => (need[r] ?? 0) > 0);

  // 2) Enumerar cuántas unidades de cada recurso se compran a cada vecino.
  const found = new Map<string, PaymentOption>();
  let anyFeasible = false;
  const leftBuy: ResourceCount = {};
  const rightBuy: ResourceCount = {};
  const ownNeed: ResourceCount = {};

  const visit = (i: number): void => {
    if (i === remaining.length) {
      if (!canCover(ctx.left, toUnits(leftBuy))) return;
      if (!canCover(ctx.right, toUnits(rightBuy))) return;
      if (!canCover(ownChoice, toUnits(ownNeed))) return;
      anyFeasible = true;
      const left = priceOf(leftBuy, ctx.prices.left);
      const right = priceOf(rightBuy, ctx.prices.right);
      const total = bank + left + right;
      if (total > ctx.coins) return;
      const key = `${left}|${right}`;
      if (!found.has(key)) {
        found.set(key, {
          buy: { left: normalizeCount(leftBuy), right: normalizeCount(rightBuy) },
          coins: { bank, left, right },
          total,
        });
      }
      return;
    }
    const r = remaining[i] as Resource;
    const k = need[r] as number;
    for (let a = 0; a <= k; a++) {
      for (let b = 0; a + b <= k; b++) {
        leftBuy[r] = a;
        rightBuy[r] = b;
        ownNeed[r] = k - a - b;
        visit(i + 1);
      }
    }
    delete leftBuy[r];
    delete rightBuy[r];
    delete ownNeed[r];
  };

  if (bank > ctx.coins) {
    // Aun así distinguimos «no hay recursos» de «no alcanza el dinero».
    visit(0);
    return { ok: false, reason: anyFeasible ? 'NOT_ENOUGH_COINS' : 'NO_RESOURCES' };
  }
  visit(0);
  if (found.size === 0) return { ok: false, reason: anyFeasible ? 'NOT_ENOUGH_COINS' : 'NO_RESOURCES' };

  // 3) Frente de Pareto en (izq, der).
  const all = [...found.values()];
  const pareto = all.filter(
    (o) =>
      !all.some(
        (p) =>
          p !== o &&
          p.coins.left <= o.coins.left &&
          p.coins.right <= o.coins.right &&
          (p.coins.left < o.coins.left || p.coins.right < o.coins.right),
      ),
  );
  pareto.sort(
    (a, b) =>
      a.total - b.total ||
      Math.abs(a.coins.left - a.coins.right) - Math.abs(b.coins.left - b.coins.right) ||
      a.coins.left - b.coins.left,
  );
  return { ok: true, options: pareto };
}

export type PaymentValidation =
  | { ok: true; coins: { bank: number; left: number; right: number } }
  | { ok: false; reason: 'INVALID_PAYMENT' | 'NOT_ENOUGH_COINS' };

/** Valida un plan de compra concreto y calcula las monedas (nunca se confía en el cliente). */
export function validatePayment(
  ctx: PaymentContext,
  cost: Cost,
  buy: { left: ResourceCount; right: ResourceCount },
): PaymentValidation {
  const need = cost.resources ?? {};
  const left = normalizeCount(buy.left ?? {});
  const right = normalizeCount(buy.right ?? {});
  const ownUnits: Resource[] = [];
  for (const r of RESOURCES) {
    const l = left[r] ?? 0;
    const rr = right[r] ?? 0;
    if (!Number.isInteger(l) || !Number.isInteger(rr) || l < 0 || rr < 0) return { ok: false, reason: 'INVALID_PAYMENT' };
    const n = need[r] ?? 0;
    if (l + rr > n) return { ok: false, reason: 'INVALID_PAYMENT' };
    for (let i = 0; i < n - l - rr; i++) ownUnits.push(r);
  }
  if (!canCover(ctx.left, toUnits(left))) return { ok: false, reason: 'INVALID_PAYMENT' };
  if (!canCover(ctx.right, toUnits(right))) return { ok: false, reason: 'INVALID_PAYMENT' };
  if (!canCover(ctx.own, ownUnits)) return { ok: false, reason: 'INVALID_PAYMENT' };
  const coins = { bank: cost.coins ?? 0, left: priceOf(left, ctx.prices.left), right: priceOf(right, ctx.prices.right) };
  if (coins.bank + coins.left + coins.right > ctx.coins) return { ok: false, reason: 'NOT_ENOUGH_COINS' };
  return { ok: true, coins };
}
